import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QtySyncDebouncer } from '../../helpers/qty-sync-debouncer';
import { CartUpdateEvent, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { PriceDiscoveryService } from '../../services/price-discovery.service';
import { environment } from '@env/environment';

@Component({
    selector: 'app-deliverit-supplier',
    templateUrl: './deliverit-supplier.component.html',
    styleUrls: ['./deliverit-supplier.component.scss']
})
export class DeliveritSupplierComponent implements OnDestroy {

    @Input() entityId = '';
    @Input() storeData: any = null;
    @Input() supplierSection: SupplierSection | null = null;

    @Output() cartUpdated = new EventEmitter<CartUpdateEvent>();
    @Output() iframeClosed = new EventEmitter<void>();

    // Cart state
    apiCartCount = 0;
    apiCartItems: SupplierProduct[] = [];
    cartQuantityMap = new Map<string, number>();
    deliveryCharge = 0;
    smallCartCharge = 0;
    smallCartWaiveOffText = '';

    // Iframe properties
    showIframe = false;
    isIframeExpanded = false;
    iframeUrlSafe: SafeResourceUrl | null = null;

    private destroy$ = new Subject<void>();
    private qtyDebouncer = new QtySyncDebouncer(600);
    private cartRequestCount = 0;

    get isCartSyncing(): boolean {
        return this.cartRequestCount > 0 || this.qtyDebouncer.hasPending();
    }

    constructor(
        private sanitizer: DomSanitizer,
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnDestroy(): void {
        this.qtyDebouncer.clearAll();
        this.destroy$.next();
        this.destroy$.complete();
    }

    initialize(): void {
        if (this.storeData && this.entityId) {
            this.fetchCart();
        }
    }

    resetState(): void {
        this.qtyDebouncer.clearAll();
        this.apiCartCount = 0;
        this.apiCartItems = [];
        this.cartQuantityMap.clear();
        this.showIframe = false;
        this.isIframeExpanded = false;
        this.iframeUrlSafe = null;
        this.deliveryCharge = 0;
        this.smallCartCharge = 0;
        this.smallCartWaiveOffText = '';
    }

    fetchCart(): Promise<boolean> {
        if (!this.entityId || !this.storeData) return Promise.resolve(false);

        return new Promise((resolve) => {
        const storeId = this.storeData.storeId || this.storeData.storeUuid || '';
        this.priceDiscoveryService.getDeliverItCart(storeId, this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const data = response?.response || response;
                    let products: SupplierProduct[] = [];
                    let cartCount = 0;

                    this.deliveryCharge = Number(data?.delivery_charge || data?.deliveryCharge || 0);
                    this.smallCartCharge = Number(data?.small_cart_charge || data?.smallCartCharge || 0);
                    this.smallCartWaiveOffText = data?.small_cart_waive_off_text || data?.smallCartWaiveOffText || '';

                    const cartProducts = data?.cart_products || data?.cartProducts;
                    if (cartProducts && Array.isArray(cartProducts)) {
                        products = cartProducts.map((cp: any) => {
                            const p = cp.product_details || cp.productDetails || {};
                            const qty = cp.quantity || 1;
                            const rate = Number(p.discounted_price_per_unit || p.discountedPricePerUnit || p.price_per_unit || p.pricePerUnit || 0);
                            const mrp = Number(p.price_per_unit || p.pricePerUnit || rate || 0);

                            const taxes = p.taxes || [];
                            const gstRate = taxes.find((t: any) => t.name === 'gst')?.rate || 0;
                            const cessRate = taxes.find((t: any) => t.name === 'cess')?.rate || 0;
                            const totalTaxRate = Number(gstRate) + Number(cessRate);
                            let cartPricePerUnit = `₹${rate}`;
                            if (totalTaxRate > 0) {
                                cartPricePerUnit += ` (incl. ${totalTaxRate}% tax)`;
                            }

                            return {
                                item_name: p.product_name || p.productName || '',
                                sku_id: String(p.product_id || p.productId || p.productUniqueId || ''),
                                rate: rate,
                                baseRate: rate,
                                mrp: mrp,
                                price: rate,
                                unit: p.oum || p.uom || p.unit || 'unit',
                                quantity: qty,
                                platform: 'deliverit',
                                isBestMatch: false,
                                is_in_stock: p.in_stock ?? true,
                                selectedQuantity: qty,
                                image_url: p.product_image || p.productImage || '',
                                cart_price_per_unit: cartPricePerUnit
                            } as SupplierProduct;
                        });
                        cartCount = data.cart_count ?? data.cartCount ?? products.reduce((sum, p) => sum + p.selectedQuantity, 0);
                    } else {
                        const itemsList = data?.items || data?.products || [];
                        products = itemsList.map((p: any) => {
                            const rate = Number(p.price || p.discountedPricePerUnit || p.discounted_price_per_unit || p.pricePerUnit || p.price_per_unit || 0);
                            const mrp = Number(p.pricePerUnit || p.price_per_unit || rate || 0);
                            const qty = Number(p.quantity || p.productQuantity || p.product_quantity || 1);
                            const cartPricePerUnit = `₹${rate}`;

                            return {
                                item_name: p.name || p.productName || p.product_name || '',
                                sku_id: String(p.sku || p.productId || p.product_id || p.productUniqueId || ''),
                                rate: rate,
                                baseRate: rate,
                                mrp: mrp,
                                price: rate,
                                unit: p.uom || p.unit || 'unit',
                                quantity: qty,
                                platform: 'deliverit',
                                isBestMatch: false,
                                is_in_stock: true,
                                selectedQuantity: qty,
                                image_url: p.product_image || p.productImage || p.image_url || p.imageUrl || '',
                                cart_price_per_unit: cartPricePerUnit
                            } as SupplierProduct;
                        });
                        cartCount = data?.cart_count ?? data?.cartCount ?? products.reduce((sum, p) => sum + p.selectedQuantity, 0);
                    }

                    if (products.length === 0) {
                        this.apiCartCount = 0;
                        this.apiCartItems = [];
                        this.cartQuantityMap.clear();
                        this.deliveryCharge = 0;
                        this.smallCartCharge = 0;
                        this.smallCartWaiveOffText = '';
                        this.emitCartUpdate();
                        resolve(true);
                        return;
                    }

                    this.apiCartCount = cartCount;
                    this.apiCartItems = products;

                    // Build quantity map
                    this.cartQuantityMap.clear();
                    this.apiCartItems.forEach(item => {
                        this.cartQuantityMap.set(String(item.sku_id), item.selectedQuantity);
                    });

                    // Update selectedQuantity on matching items in the supplier section
                    if (this.supplierSection) {
                        this.supplierSection.items.forEach(item => {
                            const skuId = String(item.sku_id || '');
                            if (this.qtyDebouncer.isPending(skuId)) {
                                return;
                            }
                            if (this.cartQuantityMap.has(skuId)) {
                                item.selectedQuantity = this.cartQuantityMap.get(skuId) || 0;
                            } else {
                                item.selectedQuantity = 0;
                            }
                        });
                    }

                    // Save to localStorage
                    try {
                        const cartData = this.apiCartItems.map(item => ({
                            product_id: item.sku_id,
                            product_name: item.item_name,
                            quantity: item.selectedQuantity,
                            price: item.rate
                        }));
                        localStorage.setItem('deliveritCart', JSON.stringify(cartData));
                    } catch (error) {
                        console.error('Failed to save DeliverIt cart to localStorage:', error);
                    }

                    this.emitCartUpdate();
                    resolve(true);
                },
                error: (error) => {
                    console.error('❌ Failed to fetch DeliverIt cart:', error);
                    resolve(false);
                }
            });
        });
    }

    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.performCartApi(item, baseline, supplierName, true);
        });
        this.syncCartLocally(String(item.sku_id));
    }

    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.performCartApi(item, baseline, 'DeliverIt', false);
        });
        this.syncCartLocally(String(item.sku_id));
    }

    flushPendingQuantitySyncs(): void {
        this.qtyDebouncer.flushAll();
    }

    /** Drops queued quantity edits without sending them. Used before clearing the cart. */
    cancelPendingQuantitySyncs(): void {
        this.qtyDebouncer.clearAll();
    }

    /** Hides the local cart immediately. The next fetchCart replaces it with the server cart. */
    stageClearedCart(): void {
        this.apiCartItems = [];
        this.apiCartCount = 0;
        this.cartQuantityMap.clear();
        this.emitCartUpdate();
    }

    /**
     * Removes one line immediately. Does not refresh the cart; the parent GETs once
     * after every removal in the batch has settled.
     */
    removeLineNow(item: SupplierProduct): Promise<void> {
        return new Promise((resolve, reject) => {
            this.priceDiscoveryService.updateDeliverItCart(String(item.sku_id), 0, this.entityId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => resolve(),
                    error: (error) => reject(error),
                });
        });
    }

    private performCartApi(item: SupplierProduct, previousQuantity: number, supplierName: string, isFirstAdd: boolean): void {
        const skuId = String(item.sku_id);
        const qty = item.selectedQuantity || 0;
        item.isAddingToCart = true;
        this.cartRequestCount += 1;

        this.priceDiscoveryService.updateDeliverItCart(skuId, qty, this.entityId)
            .subscribe({
                next: (response) => {
                    console.log('✅ DeliverIt cart updated:', response);
                    this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                    item.isAddingToCart = false;
                    if (isFirstAdd && qty > 0) {
                        this.sharedService.successAlert(`Added "${item.item_name}" from ${supplierName} to cart`);
                    } else if (qty > previousQuantity && previousQuantity === 0) {
                        this.sharedService.successAlert(`Added "${item.item_name}" to cart`);
                    }
                    if (!this.qtyDebouncer.isPending(skuId)) {
                        this.fetchCart();
                    }
                },
                error: (error) => {
                    console.error('❌ Failed to update DeliverIt cart:', error);
                    this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                    item.isAddingToCart = false;
                    if (this.qtyDebouncer.isPending(skuId)) {
                        return;
                    }
                    item.selectedQuantity = previousQuantity;
                    this.syncCartLocally(skuId);
                    const errorMessage = error?.error?.message || error?.message || 'Failed to update cart. Please try again.';
                    item.cartError = errorMessage;
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    syncCartLocally(modifiedSkuId?: string): void {
        const searchItems = this.supplierSection?.items || [];
        const activeSkuIds = new Set<string>();

        searchItems.forEach(item => {
            if (item.selectedQuantity && item.selectedQuantity > 0) {
                activeSkuIds.add(String(item.sku_id));
            }
        });
        if (modifiedSkuId) {
            activeSkuIds.add(String(modifiedSkuId));
        }

        const unchangedApiItems = this.apiCartItems.filter(
            ai => !activeSkuIds.has(String(ai.sku_id))
        );

        const searchItemsInCart = searchItems.filter(
            item => item.selectedQuantity && item.selectedQuantity > 0
        );

        this.apiCartItems = [
            ...unchangedApiItems,
            ...searchItemsInCart.map(item => ({
                item_name: item.item_name,
                sku_id: item.sku_id,
                rate: item.rate,
                baseRate: item.baseRate || item.rate,
                mrp: item.mrp || 0,
                price: item.price || 0,
                unit: item.unit,
                quantity: item.selectedQuantity || 1,
                platform: 'deliverit',
                isBestMatch: item.isBestMatch || false,
                is_in_stock: true,
                selectedQuantity: item.selectedQuantity || 1,
                image_url: item.image_url || '',
                cart_price_per_unit: item.cart_price_per_unit || `₹${item.rate}`
            } as SupplierProduct))
        ];

        this.apiCartCount = this.apiCartItems.length;

        // Save to localStorage
        try {
            const cartData = this.apiCartItems.map(item => ({
                product_id: item.sku_id,
                product_name: item.item_name,
                quantity: item.selectedQuantity,
                price: item.rate
            }));
            localStorage.setItem('deliveritCart', JSON.stringify(cartData));
        } catch (error) {
            console.error('Failed to save DeliverIt cart to localStorage:', error);
        }

        this.emitCartUpdate();
    }

    proceedToBuy(): void {
        this.flushPendingQuantitySyncs();
        if (!environment.checkoutEnabledForDeliverit) {
            this.sharedService.successAlert('Will get back to you soon');
            return;
        }

        if (!this.storeData) {
            console.error('❌ DeliverIt storeData is not available');
            this.sharedService.errorAlert('DeliverIt integration setup is incomplete.');
            return;
        }

        const store = this.storeData;
        const token = store.token || '';

        const baseUrl = environment.deliveritCheckoutUrl;
        const checkoutUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;

        console.log('🔗 [DeliverIt Checkout Iframe URL]:', checkoutUrl);

        this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(checkoutUrl);
        this.showIframe = true;
        this.isIframeExpanded = true;
        document.body.style.overflow = 'hidden';

        if (this.supplierSection) {
            this.supplierSection.items.forEach(item => {
                item.selectedQuantity = 0;
            });
        }
        this.sharedService.successAlert('Opening DeliverIt checkout');
    }

    closeIframe(): void {
        this.showIframe = false;
        this.isIframeExpanded = false;
        this.iframeUrlSafe = null;
        document.body.style.overflow = '';
        this.fetchCart();
        this.iframeClosed.emit();
    }

    reloadIframe(): void {
        if (this.iframeUrlSafe) {
            const currentUrl = this.iframeUrlSafe;
            this.iframeUrlSafe = null;
            setTimeout(() => {
                this.iframeUrlSafe = currentUrl;
            }, 10);
        }
    }

    private emitCartUpdate(): void {
        this.cartUpdated.emit({
            platform: 'deliverit',
            cartCount: this.apiCartCount,
            cartItems: this.apiCartItems
        });
    }
}
